import type { NowPlayingResponse } from '@/lib/api-types';
import { useLanguage } from '@/shared/contexts/LanguageContext';
import ExternalLink from '@/shared/ui/external-link';
import { Music } from 'lucide-react';
import { useEffect, useState } from 'react';

type State =
  | { status: 'loading' }
  | { status: 'unavailable' }
  | { status: 'ready'; data: NowPlayingResponse };

const POLL_INTERVAL = 45_000;

function useNowPlaying(): State {
  const [state, setState] = useState<State>({ status: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    let timer: ReturnType<typeof setInterval> | undefined;

    const poll = async () => {
      try {
        const response = await fetch('/api/spotify/now-playing', { signal: controller.signal });
        if (!response.ok) throw new Error(String(response.status));
        const data = (await response.json()) as NowPlayingResponse;
        setState({ status: 'ready', data });
      } catch {
        if (!controller.signal.aborted) setState({ status: 'unavailable' });
      }
    };

    // 页面不可见时定时器只跳过本轮；重新可见时立即刷新
    const tick = () => {
      if (document.visibilityState === 'visible') void poll();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') void poll();
    };

    void poll();
    timer = setInterval(tick, POLL_INTERVAL);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      controller.abort();
      if (timer) clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  return state;
}

/** 播放中的动态均衡器小动画 */
function Equalizer() {
  return (
    <span className="flex items-end gap-0.5 h-3.5" aria-hidden>
      {[0, 1, 2].map(i => (
        <span
          key={i}
          className="w-0.5 bg-green-500 rounded-full animate-pulse"
          style={{
            height: `${60 + i * 20}%`,
            animationDelay: `${i * 150}ms`,
            animationDuration: '900ms'
          }}
        />
      ))}
    </span>
  );
}

export default function NowPlaying() {
  const state = useNowPlaying();
  const { language } = useLanguage();
  const zh = language === 'zh';

  if (state.status === 'loading') {
    return (
      <div className="flex items-center gap-3 text-muted-foreground">
        <div className="w-12 h-12 rounded-lg bg-muted/40 animate-pulse" />
        <div className="space-y-2">
          <div className="h-3 w-32 bg-muted/40 rounded animate-pulse" />
          <div className="h-3 w-20 bg-muted/40 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  if (state.status === 'unavailable') {
    return (
      <p className="text-sm text-muted-foreground inline-flex items-center gap-2">
        <Music className="w-4 h-4" />
        {zh ? '暂时听不到我在听什么。' : 'Nothing to report right now.'}
      </p>
    );
  }

  const { data } = state;
  const content = (
    <div className="flex items-center gap-3">
      {data.albumArtUrl ? (
        <img
          src={data.albumArtUrl}
          alt={data.album}
          className="w-12 h-12 rounded-lg object-cover shadow-sm"
        />
      ) : (
        <div className="w-12 h-12 rounded-lg bg-muted/40 flex items-center justify-center">
          <Music className="w-5 h-5 text-muted-foreground" />
        </div>
      )}
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground truncate flex items-center gap-2">
          {data.title}
          {data.isPlaying && <Equalizer />}
        </p>
        <p className="text-xs text-muted-foreground truncate">{data.artist}</p>
        <p className="text-xs text-muted-foreground/70 mt-0.5">
          {data.isPlaying ? (zh ? '正在播放' : 'Now playing') : zh ? '最近播放' : 'Last played'}
        </p>
      </div>
    </div>
  );

  return data.trackUrl ? (
    <ExternalLink href={data.trackUrl} className="block hover:opacity-80 transition-opacity">
      {content}
    </ExternalLink>
  ) : (
    content
  );
}
